import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MatProgressBar } from '@angular/material';
import { ObservableMedia } from '@angular/flex-layout';

import { debounceTime } from 'rxjs/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { CollegeCourseInfo, EmployeeInfo, UserSearchResult } from '../application/application.service';
import { NotificationTermInfo } from '../notification/notification.service';
import { UserInfo } from '../user/user.service';

import {
    AdoptionCourseSearchDialogComponent,
    BaseAdoptionCourseSearchDialogOptions,
} from './adoptioncoursesearch/adoptioncoursesearch.dialogcomponent';
import { CheckboxDialogComponent, _CheckboxDialogOptions } from './checkbox/checkbox.dialogcomponent';
import {
    ClassicNotificationDialogComponent,
    BaseClassicNotificationDialogOptions,
} from './classicnotification/classicnotification.dialogcomponent';
import { CountdownDialogComponent, _CountdownDialogOptions } from './countdown/countdown.dialogcomponent';
import {
    CourseChoiceDialogComponent,
    BaseCourseChoiceDialogOptions,
    BaseCourseChoiceResult,
} from './coursechoice/coursechoice.dialogcomponent';
import {
    CourseDescriptionDialogComponent,
    _CourseDescriptionDialogOptions,
} from './coursedescription/coursedescription.dialogcomponent';
import { CourseNoteDialogComponent, _CourseNoteDialogOptions } from './coursenote/coursenote.dialogcomponent';
import { ConfirmationDialogComponent, _ConfirmationDialogOptions } from './confirmation/confirmation.dialogcomponent';
import { EligibleCoursesDialogComponent, _EligibleCoursesDialogOptions } from './eligiblecourses/eligiblecourses.dialogcomponent';
import { FullScheduleDialogComponent, BaseFullScheduleDialogOptions } from './fullschedule/fullschedule.dialogcomponent';
import { InputBoxDialogComponent, _InputBoxDialogOptions } from './inputbox/inputbox.dialogcomponent';
import { MessageBoxDialogComponent, BaseMessageBoxDialogOptions } from './messagebox/messagebox.dialogcomponent';
import { NotificationDialogComponent, BaseNotificationDialogOptions } from './notification/notification.dialogcomponent';
import { ProxyApproveDialogComponent, BaseProxyApproveDialogOptions } from './proxyapprove/proxyapprove.dialogcomponent';
import {
    ReportOptionsDialogComponent,
    BaseReportOptionsDialogOptions,
    BaseReportRunOptions,
} from './reportoptions/reportoptions.dialogcomponent';
import { ScheduleCourseDialogComponent, _ScheduleCourseDialogOptions } from './schedulecourse/schedulecourse.dialogcomponent';
import { StudentSearchDialogComponent, BaseStudentSearchDialogOptions } from './studentsearch/studentsearch.dialogcomponent';
import { OptionBoxDialogComponent, BaseOptionBoxDialogOptions, BaseOptionBoxOption } from './optionbox/optionbox.dialogcomponent';
import { TextNoteDialogComponent, _TextNoteDialogOptions } from './textnote/textnote.dialogcomponent';
import {
    UserSearchDialogComponent,
    BaseUserSearchDialogOptions,
    BaseUserSearchResultInfo,
} from './usersearch/usersearch.dialogcomponent';
import { EditTestThresholdDialogComponent, _EditTestThresholdDialogOptions } from './edittestthreshold/edittestthreshold.dialogcomponent';
import { _UpdateCollegeIDDialogOptions, UpdateCollegeIDDialogComponent } from './updatecollegeID/updatecollegeID.dialogcomponent';

export { BaseAdoptionCourseSearchDialogOptions as AdoptionCourseSearchDialogOptions };
export { _CheckboxDialogOptions as CheckboxDialogOptions };
export { BaseClassicNotificationDialogOptions as ClassicNotificationDialogOptions };
export { _CountdownDialogOptions as CountdownDialogOptions };
export { _ConfirmationDialogOptions as ConfirmationDialogOptions };
export { BaseCourseChoiceDialogOptions as CourseChoiceDialogOptions };
export { BaseCourseChoiceResult as CourseChoiceResult };
export { _CourseDescriptionDialogOptions as CourseDescriptionDialogOptions };
export { _CourseNoteDialogOptions as CourseNoteDialogOptions };
export { _EligibleCoursesDialogOptions as EligibleCourseDialogOptions };
export { _EditTestThresholdDialogOptions as EditTestThresholdDialogOptions };
export { BaseFullScheduleDialogOptions as FullScheduleDialogOptions };
export { _InputBoxDialogOptions as InputBoxDialogOptions };
export { BaseMessageBoxDialogOptions as MessageBoxDialogOptions };
export { BaseNotificationDialogOptions as NotificationDialogOptions };
export { BaseOptionBoxDialogOptions as OptionBoxDialogOptions };
export { BaseOptionBoxOption as OptionBoxOption };
export { BaseProxyApproveDialogOptions as ProxyApproveDialogOptions };
export { BaseReportOptionsDialogOptions as ReportOptionsDialogOptions };
export { BaseReportRunOptions as ReportRunOptions };
export { _ScheduleCourseDialogOptions as ScheduleCourseDialogOptions };
export { BaseStudentSearchDialogOptions as StudentSearchDialogOptions };
export { _TextNoteDialogOptions as TextNoteDialogOptions };
export { BaseUserSearchDialogOptions as UserSearchDialogOptions };
export { BaseUserSearchResultInfo as UserSearchResultInfo };

@Injectable()
export class DialogService {
    private openHandle: MatDialogRef<any>;

    smallMediaSize: boolean;

    constructor(private matDialog: MatDialog, private mediaChange: ObservableMedia) {
        this.smallMediaSize = mediaChange.isActive('screen and (max-width: 959px)');

        // Observe for media changes
        this.mediaChange.subscribe((media) => {
            // Set flags accordingly with what we know.
            this.smallMediaSize = media.mqAlias === 'xs' || media.mqAlias === 'sm';
        });
    }

    // #region Close All Dialogs Service Call

    CloseAllDialogs() {
        this.matDialog.closeAll();
    }

    // #endregion

    // #region Open Adoption Course Search Dialog Service Call

    OpenAdoptionCourseSearchDialog(options?: BaseAdoptionCourseSearchDialogOptions): Observable<CollegeCourseInfo> {
        const self = this;

        const openHandle = this.matDialog.open<AdoptionCourseSearchDialogComponent>(AdoptionCourseSearchDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Select Course',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '900px',
            height: self.smallMediaSize ? '80vh' : '700px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Checkbox Dialog Service Call

    OpenCheckboxDialog(options?: _CheckboxDialogOptions): Observable<boolean> {
        const openHandle = this.matDialog.open<CheckboxDialogComponent>(CheckboxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: options.width || '800px',
            height: options.height || '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Classic Notification Dialog Service Call

    OpenClassicNotificationDialog(options?: BaseClassicNotificationDialogOptions): Observable<NotificationTermInfo> {
        const self = this;

        const openHandle = this.matDialog.open<ClassicNotificationDialogComponent>(ClassicNotificationDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Notification Details',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '85vw',
            height: self.smallMediaSize ? '80vh' : '500px',
            maxHeight: '85vh',
            maxWidth: '85vw'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Confirmation Dialog Service Call

    OpenConfirmationDialog(options?: _ConfirmationDialogOptions): Observable<boolean> {
        const self = this;
        const dlgoptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Confirmation Requested...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            panelClass: 'confdialog',
            maxHeight: '85vh',
            // width: '80vw'
            // ,
            // height: (self.smallMediaSize ? '80vh' : '600px')
        };

        if (self.smallMediaSize) {
            dlgoptions.width = '80vw';
            dlgoptions.height = '80vh';
            dlgoptions.panelClass = ['confdialog', 'smalldialogpanel'];
        }

        const openHandle = this.matDialog.open<ConfirmationDialogComponent>(ConfirmationDialogComponent, dlgoptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Countdown Dialog Service Call

    OpenCountdownDialog(options?: _CountdownDialogOptions): Observable<boolean> {
        const openHandle = this.matDialog.open<CountdownDialogComponent>(CountdownDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Please Wait...',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                Time: 60,
                ...options,
            },
            width: '500px',
            height: '18rem',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Course Choice Dialog Service Call

    OpenCourseChoiceDialog(options?: BaseCourseChoiceDialogOptions): Observable<BaseCourseChoiceResult> {
        const self = this;

        const openHandle = this.matDialog.open<CourseChoiceDialogComponent>(CourseChoiceDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Select Course',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '900px',
            height: self.smallMediaSize ? '80vh' : '700px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Course Description Dialog Service Call

    OpenCourseDescriptionDialog(options?: _CourseDescriptionDialogOptions): Observable<boolean> {
        const self = this;

        const openHandle = this.matDialog.open<CourseDescriptionDialogComponent>(CourseDescriptionDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Course Information',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '900px',
            height: self.smallMediaSize ? '80vh' : '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Course Note Dialog Service Call

    OpenCourseNoteDialog(options?: _CourseNoteDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<CourseNoteDialogComponent>(CourseNoteDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Optional Note',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Eligible Courses Dialog Service Call

    OpenEditTestThresholdDialog(options?: _EditTestThresholdDialogOptions): Observable<number | null> {
        const openHandle = this.matDialog.open<EditTestThresholdDialogComponent>(EditTestThresholdDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Edit Test Score',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Eligible Courses Dialog Service Call

    OpenEligibleCoursesDialog(options?: _EligibleCoursesDialogOptions): Observable<number[] | null> {
        const openHandle = this.matDialog.open<EligibleCoursesDialogComponent>(EligibleCoursesDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Select Eligibility Courses',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Input Box Dialog Service Call

    OpenInputBoxDialog(options?: _InputBoxDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<InputBoxDialogComponent>(InputBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested...',
                MessageText: '',
                InputHint: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Message Box Dialog Service Call

    OpenMessageBoxDialog(options?: BaseMessageBoxDialogOptions): Observable<boolean> {
        const self = this;

        const openHandle = self.matDialog.open<MessageBoxDialogComponent>(MessageBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : options.width || '600px',
            height: self.smallMediaSize ? '80vh' : options.height || '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Notification Dialog Service Call

    OpenNotificationDialog(options?: BaseNotificationDialogOptions): Observable<NotificationTermInfo> {
        const self = this;

        const openHandle = this.matDialog.open<NotificationDialogComponent>(NotificationDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Notification Details',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '900px',
            height: self.smallMediaSize ? '80vh' : '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Option Box Dialog Service Call

    OpenOptionBoxDialog(options?: BaseOptionBoxDialogOptions): Observable<any> {
        const openHandle = this.matDialog.open<OptionBoxDialogComponent>(OptionBoxDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Please select',
                MessageText: '',
                Options: [],
                ...options,
            },
            maxHeight: '85vh',
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Proxy Approve Dialog Service Call

    OpenProxyApproveDialog(options?: BaseProxyApproveDialogOptions): Observable<any> {
        const self = this;

        const openHandle = this.matDialog.open<ProxyApproveDialogComponent>(ProxyApproveDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Information Requested',
                MessageText: '',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '700px',
            height: self.smallMediaSize ? '80vh' : '400px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Report Options Dialog Service Call

    OpenReportOptionsDialog(options?: BaseReportOptionsDialogOptions): Observable<BaseReportRunOptions> {
        const self = this;

        const openHandle = this.matDialog.open<ReportOptionsDialogComponent>(ReportOptionsDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Choose Report Options',
                MessageText: '',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : options.width || '700px',
            height: self.smallMediaSize ? '80vh' : options.height || '200px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Schedule All Courses Dialog Service Call

    OpenScheduleAllCoursesDialog(options?: BaseFullScheduleDialogOptions): Observable<any> {
        const openHandle = this.matDialog.open<FullScheduleDialogComponent>(FullScheduleDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Schedule All Courses',
                MessageText: '',
                ...options,
            },
            width: options.width || '900px',
            height: options.height || '500px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Schedule Course Dialog Service Call

    OpenScheduleCourseDialog(options?: _ScheduleCourseDialogOptions): Observable<any> {
        const self = this;

        const dlgOptions: MatDialogConfig<any> = {
            disableClose: true,
            data: {
                TitleText: 'Schedule Course',
                MessageText: '',
                ...options,
            },
            width: options.width || '900px',
            height: options.height || '400px',
            maxHeight: '85vh',
        };

        if (self.smallMediaSize) {
            dlgOptions.width = '80vw';
            dlgOptions.height = '80vh';
        }

        const openHandle = this.matDialog.open<ScheduleCourseDialogComponent>(ScheduleCourseDialogComponent, dlgOptions);

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Student Search Dialog Service Call

    OpenStudentSearchDialog(options?: BaseStudentSearchDialogOptions): Observable<BaseReportRunOptions> {
        const self = this;

        const openHandle = this.matDialog.open<StudentSearchDialogComponent>(StudentSearchDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'DELTA Student Search',
                MessageText: '',
                ...options,
            },
            width: self.smallMediaSize ? '80vw' : '900px',
            height: self.smallMediaSize ? '80vh' : '750px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Text Note Dialog Service Call

    OpenTextNoteDialog(options?: _TextNoteDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<TextNoteDialogComponent>(TextNoteDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Optional Note',
                MessageText: '',
                OKText: 'OK',
                ...options,
            },
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open Update College ID Dialog Service Call

    OpenUpdateCollegeIDDialog(options?: _UpdateCollegeIDDialogOptions): Observable<string> {
        const openHandle = this.matDialog.open<UpdateCollegeIDDialogComponent>(UpdateCollegeIDDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'Update College ID...',
                MessageText: '',
                InputHint: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            maxHeight: '85vh',
            // width: '500px',
            // height: (options && options.height) || '13rem'
        });

        return openHandle.afterClosed();
    }

    // #endregion

    // #region Open User Search Dialog Service Call

    OpenUserSearchDialog(options?: BaseUserSearchDialogOptions): Observable<BaseUserSearchResultInfo> {
        const openHandle = this.matDialog.open<UserSearchDialogComponent>(UserSearchDialogComponent, {
            disableClose: true,
            data: {
                TitleText: 'County Employee User Search',
                MessageText: '',
                OKText: 'OK',
                CancelText: 'Cancel',
                ...options,
            },
            width: options.width || '1000px',
            height: options.height || '750px',
            maxHeight: '85vh',
        });

        return openHandle.afterClosed();
    }

    // #endregion
}

export class BaseDialogOptions {
    TitleText?: string;
    MessageText?: string;
    OKText?: string;
    CancelText?: string;
    IconColor?: string;
    IconName?: string;
    ShowIcon?: boolean;
    height?: string;
    width?: string;
}



// WEBPACK FOOTER //
// ./src/services/dialogs/dialogs.service.ts